import { Avatar, Input, TextField } from "@heroui/react";
import { AnimatePresence, m, useReducedMotion } from "framer-motion";
import { memo, useEffect, useState } from "react";
import { ContinueButton } from "@funnel-ui/shell/ContinueButton";

export type FirstMessagePreviewProps = {
  characterName: string;
  /** Avatar src. When missing, an initial-based fallback is rendered. */
  characterAvatarUrl?: string;
  /** The (templated or generated) opening message body. */
  message: string;
  /** CTA copy — "Start chatting" / "Unlock full conversation" / "Continue". */
  ctaLabel: string;
  onCta: () => void;
  /**
   * How long the typing dots should run before the message fades in.
   * Defaults to 700ms; capped at 1200ms to stay within the funnel motion budget.
   * Honors `prefers-reduced-motion` (collapses to instant).
   */
  typingMs?: number;
  /** Placeholder shown inside the disabled input preview. */
  disabledInputPlaceholder?: string;
  className?: string;
  testId?: string;
};

const TYPING_MS_CAP = 1200;
const FADE_MS = 220;

const TypingDots = ({ testId }: { testId?: string }) => (
  <span
    aria-hidden="true"
    data-testid={testId ? `${testId}-typing-dots` : undefined}
    className="inline-flex items-center gap-1 px-1 py-1"
  >
    {[0, 1, 2].map((i) => (
      <span
        key={i}
        className="block h-1.5 w-1.5 rounded-full"
        style={{
          background: "hsl(var(--brand-bg-foreground) / 0.55)",
          animation: `fc-typing-bounce 900ms ${i * 120}ms infinite ease-in-out`,
        }}
      />
    ))}
  </span>
);

type AvatarSlotProps = {
  characterName: string;
  characterAvatarUrl?: string;
};

const AvatarSlot = ({ characterName, characterAvatarUrl }: AvatarSlotProps) => {
  const initial = characterName.trim().slice(0, 1).toUpperCase() || "?";
  return (
    <Avatar size="md" className="shrink-0">
      {characterAvatarUrl && <Avatar.Image src={characterAvatarUrl} alt="" />}
      <Avatar.Fallback>{initial}</Avatar.Fallback>
    </Avatar>
  );
};

type MessageBubbleProps = {
  message: string;
  showTyping: boolean;
  prefersReduced: boolean;
  testId?: string;
};

const MessageBubble = ({ message, showTyping, prefersReduced, testId }: MessageBubbleProps) => (
  <div
    data-testid={testId ? `${testId}-bubble` : undefined}
    className="relative max-w-[85%] rounded-2xl rounded-tl-md px-4 py-3 text-sm leading-relaxed"
    style={{
      background: "hsl(var(--brand-bg-foreground) / 0.06)",
      color: "hsl(var(--brand-bg-foreground))",
    }}
  >
    <AnimatePresence initial={false} mode="popLayout">
      {showTyping ? (
        <m.span
          key="typing"
          initial={prefersReduced ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={prefersReduced ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: prefersReduced ? 0 : FADE_MS / 1000 }}
          className="inline-flex"
        >
          <TypingDots testId={testId} />
        </m.span>
      ) : (
        <m.span
          key="text"
          initial={prefersReduced ? false : { opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: prefersReduced ? 0 : FADE_MS / 1000,
            ease: [0.32, 0.72, 0, 1],
          }}
          data-testid={testId ? `${testId}-message` : undefined}
        >
          {message}
        </m.span>
      )}
    </AnimatePresence>
  </div>
);

const DisabledInputPreview = ({ placeholder }: { placeholder: string }) => (
  <TextField
    isDisabled
    aria-label="Reply preview"
    className="pointer-events-none w-full opacity-70"
  >
    <Input placeholder={placeholder} readOnly />
  </TextField>
);

const useTypingState = (typingMs: number, prefersReduced: boolean): boolean => {
  const [showTyping, setShowTyping] = useState<boolean>(() => !prefersReduced && typingMs > 0);

  useEffect(() => {
    if (prefersReduced || typingMs <= 0) {
      setShowTyping(false);
      return;
    }
    setShowTyping(true);
    const cappedDuration = Math.min(typingMs, TYPING_MS_CAP);
    const handle = setTimeout(() => setShowTyping(false), cappedDuration);
    return () => clearTimeout(handle);
  }, [prefersReduced, typingMs]);

  return showTyping;
};

const FirstMessagePreviewImpl = ({
  characterName,
  characterAvatarUrl,
  message,
  ctaLabel,
  onCta,
  typingMs = 700,
  disabledInputPlaceholder = "Continue to reply…",
  className,
  testId,
}: FirstMessagePreviewProps) => {
  const prefersReduced = useReducedMotion();
  const showTyping = useTypingState(typingMs, !!prefersReduced);

  return (
    <section
      data-testid={testId}
      className={`flex flex-col gap-6 ${className ?? ""}`}
      aria-label={`First message preview from ${characterName}`}
    >
      <div className="flex items-start gap-3">
        <AvatarSlot characterName={characterName} characterAvatarUrl={characterAvatarUrl} />
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <span
            className="text-sm font-semibold"
            style={{ color: "hsl(var(--brand-bg-foreground))" }}
          >
            {characterName}
          </span>
          <MessageBubble
            message={message}
            showTyping={showTyping}
            prefersReduced={!!prefersReduced}
            testId={testId}
          />
        </div>
      </div>

      <DisabledInputPreview placeholder={disabledInputPlaceholder} />

      <ContinueButton label={ctaLabel} onPress={onCta} />
    </section>
  );
};

export const FirstMessagePreview = memo(FirstMessagePreviewImpl);

export default FirstMessagePreview;
